import React from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';

import { Panel } from '../../../../../components/atoms/Panel';

import { LocationPanelProps } from './LocationPanel.props';
import {
  Field,
  OptionalField,
  labelSx,
  formatAddress,
  getContactPhone,
  getContactName,
  findAttr,
  formatAttrAddress,
  formatAttrContact,
  extractReferences,
} from './LocationHelpers';
import { EditableAddress, MapSquare } from './AddressSection';
import {
  parseTradingHours,
  parseCoverageHours,
  CombinedHoursDisplay,
  EMPTY_TRADING_HOURS,
} from './TradingHoursSection';
import { findNodeByType, findNodeById, HierarchyNode } from './HierarchyTree';

// ── Main Component ───────────────────────────────────────────

export const LocationPanel: React.FC<LocationPanelProps> = ({
  service,
  nodeAttributes,
  tradingHoursData,
  coverageHoursData,
  onSaveAddress,
}) => {
  const hierarchy = service?.locationHierarchy ?? null;
  const mainNodeId = service?.mainLocationNodeId ?? 0;
  const attrs = nodeAttributes ?? [];

  // Extract outlet (physical location) and terminal/sublocation from hierarchy
  const outletNode = findNodeByType(hierarchy, 'outlet');
  const mainNode = findNodeById(hierarchy, mainNodeId);
  const outletName = outletNode?.name ?? service?.mainLocation ?? '-';
  const sublocationName =
    mainNode && mainNode.id !== outletNode?.id
      ? mainNode.name
      : String(service?.subLocation ?? '-');

  // Address: prefer service-level override, fall back to node main address
  const serviceAddr = formatAddress(service?.serviceAddress);
  const mainAddrAttr = findAttr(attrs, 'address', 'Main');
  const nodeAddr = formatAttrAddress(mainAddrAttr);
  const address = serviceAddr !== '-' ? serviceAddr : nodeAddr;

  // Contact from node attributes
  const mainContact = formatAttrContact(findAttr(attrs, 'contact', 'Main'));
  const fallbackPhone = getContactPhone(service?.contactData);
  const fallbackName = getContactName(service?.contactData);
  const contactName = mainContact.name !== '-' ? mainContact.name : fallbackName;
  const phone = mainContact.phone !== '-' ? mainContact.phone : fallbackPhone;

  // Trading hours and SLA coverage from node attributes
  const tradingHours = parseTradingHours(tradingHoursData);
  const coverageHours = parseCoverageHours(coverageHoursData);

  // References from node attributes
  const locRefs = extractReferences(attrs);
  const svc = service as unknown as Record<string, unknown> | null;
  const subRefs = svc?.subLocationReferences as
    | Record<string, string>
    | undefined;
  const subRef1 = subRefs?.referenceNo1;
  const subRef2 = subRefs?.referenceNo2;
  const subRef3 = subRefs?.referenceNo3;

  return (
    <Panel title="Location">
      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
        {/* Left: Details */}
        <Box>
          <Field label="Location" value={outletName} />
          <EditableAddress
            serviceAddress={service?.serviceAddress}
            displayValue={address}
            onSave={onSaveAddress}
          />
          <Field label="Sublocation" value={sublocationName} />

          <Box sx={{ borderTop: '1px solid', borderColor: 'v4.border', my: 1 }} />

          <Field label="Contact Name" value={contactName} />
          <Field label="Contact Phone" value={phone} />

          <Box sx={{ borderTop: '1px solid', borderColor: 'v4.border', my: 1 }} />

          <OptionalField label="Location Ref #1" value={locRefs.ref1} />
          <OptionalField label="Location Ref #2" value={locRefs.ref2} />
          <OptionalField label="Location Ref #3" value={locRefs.ref3} />
          <OptionalField label="Sublocation Ref #1" value={subRef1} />
          <OptionalField label="Sublocation Ref #2" value={subRef2} />
          <OptionalField label="Sublocation Ref #3" value={subRef3} />

          <Box sx={{ borderTop: '1px solid', borderColor: 'v4.border', my: 1 }} />

          <Box sx={{ mb: 1 }}>
            <CombinedHoursDisplay
              tradingHours={tradingHours ?? EMPTY_TRADING_HOURS}
              coverageHours={coverageHours ?? EMPTY_TRADING_HOURS}
            />
          </Box>
        </Box>

        {/* Right: Map + Structure Hierarchy */}
        <Box>
          <MapSquare address={address !== '-' ? address : outletName} />

          <Box sx={{ borderTop: '1px solid', borderColor: 'v4.border', mt: 2, pt: 2 }}>
            <Typography sx={{ ...labelSx, mb: 1 }}>Structure Hierarchy</Typography>
            {hierarchy?.children?.length ? (
              hierarchy.children.map((child) => (
                <HierarchyNode key={child.id} node={child} activeNodeId={mainNodeId} />
              ))
            ) : (
              <Typography sx={{ fontSize: '12px', color: 'text.secondary' }}>
                No hierarchy data
              </Typography>
            )}
          </Box>
        </Box>
      </Box>
    </Panel>
  );
};
